/// <amd-module name="Wit/WorkItemForm/Attachments/AttachmentUtils" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { ILinkInfo } from "Wit/WorkItemDataManager/Contracts";
import { getAttachmentUri } from "./useAttachmentUri";

const imageExtensions: readonly string[] = ["png", "jpg", "jpeg", "svg", "bpm", "gif", "webp"];
const videoExtensions: readonly string[] = ["mp4", "webm"];
const pdfExtensions: readonly string[] = ["pdf"];
const textExtensions: readonly string[] = [
    "sql",
    "java",
    "jsp",
    "jar",
    "asp",
    "aspx",
    "css",
    "scss",
    "sass",
    "less",
    "html",
    "htm",
    "xhtml",
    "js",
    "jsx",
    "cs",
    "csv",
    "vb",
    "cpp",
    "cxx",
    "c++",
    "hpp",
    "hxx",
    "h",
    "f#",
    "ts",
    "tsx",
    "py",
    "md",
    "rs",
    "rb",
    "xml",
    "c",
    "php",
    "swift",
    "tex",
    "json",
    "yml",
    "yaml",
    "sbl",
    "asm",
    "d",
    "f",
    "for",
    "go",
    "m",
    "p",
    "pas",
    "r",
    "bat",
    "sh",
    "pl",
    "awk",
    "cmd",
    "lua",
    "sln",
    "proj",
    "txt",
    "log",
    "wiql"
];

export type PreviewType = "Image" | "Video" | "PDF" | "Text";

function getExtension(fileName: string): string | undefined {
    return fileName?.split(".").pop()?.toLowerCase();
}

export function getAttachmentPreviewType(fileName: string): PreviewType | undefined {
    const extension = getExtension(fileName);
    if (!extension) {
        return undefined;
    }

    if (imageExtensions.includes(extension)) {
        return "Image";
    } else if (videoExtensions.includes(extension)) {
        return "Video";
    } else if (pdfExtensions.includes(extension)) {
        return "PDF";
    } else if (textExtensions.includes(extension)) {
        return "Text";
    }

    return undefined;
}

export function getAttachmentIconName(fileName: string): string {
    const extension = getExtension(fileName);

    switch (extension) {
        case "zip":
        case "7z":
        case "pkg":
        case "rar":
        case "rpm":
        case "gz":
        case "z":
        case "tar":
            return "ZipFolder";
        case "png":
        case "jpg":
        case "bmp":
        case "gif":
        case "ico":
        case "jpeg":
        case "ai":
        case "psd":
        case "svg":
        case "tif":
        case "tiff":
        case "webp":
            return "FileImage";
        case "mp3":
        case "aif":
        case "mid":
        case "midi":
        case "mpa":
        case "ogg":
        case "wav":
        case "wma":
        case "wpl":
            return "HeadsetSolid";
        case "mp4":
        case "avi":
        case "flv":
        case "m4v":
        case "mkv":
        case "mov":
        case "mpg":
        case "mpeg":
        case "wmv":
        case "webm":
            return "Video";
        case "email":
        case "eml":
        case "emlx":
        case "msg":
            return "Mail";
        case "ttf":
        case "fnt":
        case "otf":
            return "FontSize";
        case "docx":
        case "doc":
        case "odt":
        case "rtf":
            return "WordDocument";
        case "pptx":
        case "ppt":
        case "pps":
        case "odp":
            return "PowerPointDocument";
        case "xlsx":
        case "xlsm":
        case "ods":
        case "xls":
        case "csv":
            return "ExcelDocument";
        case "pdb":
            return "FilePDB";
        case "pdf":
            return "PDF";
        case "sql":
        case "wiql":
            return "FileSQL";
        case "java":
        case "jsp":
        case "jar":
            return "FileJAVA";
        case "asp":
        case "aspx":
            return "FileASPX";
        case "CSS":
            return "FileCSS";
        case "scss":
        case "sass":
            return "FileSass";
        case "less":
            return "FileLess";
        case "html":
        case "htm":
        case "xhtml":
            return "FileHTML";
        case "js":
        case "jsx":
            return "JavaScriptLanguage";
        case "cs":
            return "CSharpLanguage";
        case "vb":
            return "VisualBasicLanguage";
        case "cpp":
        case "cxx":
        case "c++":
        case "hpp":
        case "hxx":
        case "h":
            return "CPlusPlusLanguage";
        case "f#":
            return "FSharpLanguage";
        case "ts":
        case "tsx":
            return "TypeScriptLanguage";
        case "py":
            return "PythonLanguage";
        case "md":
            return "MarkDownLanguage";
        case "rs":
            return "RustLanguage";
        case "rb":
            return "RubyGemsLogo";
        case "xml":
        case "c":
        case "php":
        case "swift":
        case "tex":
        case "json":
        case "yml":
        case "yaml":
        case "sbl":
        case "asm":
        case "d":
        case "f":
        case "for":
        case "go":
        case "m":
        case "p":
        case "pas":
        case "r":
            return "FileCode";
        case "bat":
        case "sh":
        case "pl":
        case "awk":
        case "cmd":
        case "lua":
            return "Script";
        case "sln":
        case "proj":
            return "FileTypeSolution";
        case "txt":
        case "log":
        default:
            return "TextDocument";
    }
}

// TODO:Replat this is how the existing UI does it, but I wonder if we want to show MB or GB where that applies.
export function formatFileSize(lengthInBytes: number | null | undefined): string {
    if (lengthInBytes === undefined || lengthInBytes === null) {
        return "";
    }

    const kb = Math.ceil(lengthInBytes / 1024);
    return `${kb}K`;
}

export async function downloadAttachment(pageContext: IVssPageContext, workItem: IWorkItem, attachment: ILinkInfo) {
    const uri = await getAttachmentUri(pageContext, workItem.project.guid, attachment);
    const link = document.createElement("a");
    link.href = uri;
    link.download = attachment.OriginalName ?? attachment.FilePath ?? "";
    // No need to add the link to the body
    link.click();
}
